import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Trophy, ChevronLeft, Award } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/auth';

interface LeaderboardEntry {
  user_id: string;
  score: number;
  total_questions: number;
  questions_answered: number;
  completion_rate: number;
  score_percentage: number;
  completed_at: string;
  total_response_time_ms: number;
  user_display_name?: string;
  rank?: number;
}

const GrandQuizLeaderboardPage = () => {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [selectedQuestionCount, setSelectedQuestionCount] = useState<number>(20);
  const [selectedNiveau, setSelectedNiveau] = useState<string>('');
  const [selectedMatiere, setSelectedMatiere] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const niveaux = ['6ème', '5ème', '4ème', '3ème', 'Seconde', 'Première', 'Terminale'];
  const questionCounts = [10, 20, 50, 100];

  const getRegularMatieres = (niveau: string) => {
    const baseRegularMatieres = [
      'Allemand',
      'Anglais',
      'Arts plastiques',
      'Enseignement scientifique',
      'Espagnol',
      'Français',
      'Histoire-Géo-EMC',
      'Musique',
      'Philosophie',
      'Physique-Chimie',
      'Sciences Numériques et Technologie',
      'SVT (Sciences de la Vie et de la Terre)',
      'Technologie'
    ];

    if (!niveau || ['6ème', '5ème', '4ème', '3ème', 'Seconde'].includes(niveau)) {
      baseRegularMatieres.push('Mathématiques');
    }
    if (niveau === 'Seconde') {
      baseRegularMatieres.push('SES (Sciences Économiques et Sociales)');
    }

    return baseRegularMatieres.sort();
  };

  const getSpecialtyMatieres = (niveau: string) => {
    const baseSpecialtyMatieres = [
      'Éduc. Médias et Information (EMI)',
      'Hist. Géo, Géopolitique et Sciences Pol.',
      'Humanités, Littérature et Philosophie',
      'Langues, Littératures et Cultures Étrangères',
      'Numérique et Sciences Informatiques',
      'Sciences de l\'ingénieur'
    ];

    if (['Première', 'Terminale'].includes(niveau)) {
      baseSpecialtyMatieres.push('Mathématiques');
      baseSpecialtyMatieres.push('SES (Sciences Économiques et Sociales)');
    }

    return baseSpecialtyMatieres.sort();
  };

  const optionalMatieres = [
    'Grec',
    'Latin'
  ].sort();

  const isSubjectAvailable = (matiere: string, niveau: string): boolean => {
    if (!niveau) return true;

    if (matiere === 'Technologie' && 
        !['6ème', '5ème', '4ème', '3ème'].includes(niveau)) {
      return false;
    }

    if (niveau === '6ème' && ['Latin', 'Grec'].includes(matiere)) {
      return false;
    }

    if (matiere === 'Sciences Numériques et Technologie' && niveau !== 'Seconde') {
      return false;
    }

    if (matiere === 'SES (Sciences Économiques et Sociales)') {
      if (!['Seconde', 'Première', 'Terminale'].includes(niveau)) {
        return false;
      }
      if (['Première', 'Terminale'].includes(niveau)) {
        return getSpecialtyMatieres(niveau).includes(matiere);
      }
      return getRegularMatieres(niveau).includes(matiere);
    }

    if (matiere === 'Mathématiques') {
      if (['Première', 'Terminale'].includes(niveau)) {
        return getSpecialtyMatieres(niveau).includes(matiere);
      }
      return getRegularMatieres(niveau).includes(matiere);
    }

    if (matiere === 'Enseignement scientifique' && 
        !['Première', 'Terminale'].includes(niveau)) {
      return false;
    }

    const specialtyMatieres = [
      'Hist. Géo, Géopolitique et Sciences Pol.',
      'Éduc. Médias et Information (EMI)',
      'Humanités, Littérature et Philosophie',
      'Langues, Littératures et Cultures Étrangères',
      'Numérique et Sciences Informatiques',
      'Sciences de l\'ingénieur'
    ];
    if (specialtyMatieres.includes(matiere) && 
        !['Première', 'Terminale'].includes(niveau)) {
      return false;
    }

    if (matiere === 'Philosophie' && niveau !== 'Terminale') {
      return false;
    }

    return true;
  };

  // Get available matières based on selected niveau
  const getAvailableMatieres = () => {
    const availableRegularMatieres = getRegularMatieres(selectedNiveau).filter(matiere => 
      isSubjectAvailable(matiere, selectedNiveau)
    );
    const availableSpecialtyMatieres = getSpecialtyMatieres(selectedNiveau).filter(matiere => 
      isSubjectAvailable(matiere, selectedNiveau)
    );
    const availableOptionalMatieres = optionalMatieres.filter(matiere => 
      isSubjectAvailable(matiere, selectedNiveau)
    );
    
    // Add Quiz global only for college levels
    if (['6ème', '5ème', '4ème', '3ème'].includes(selectedNiveau)) {
      return {
        quizGlobal: ['Quiz global'],
        regular: availableRegularMatieres,
        specialty: availableSpecialtyMatieres,
        optional: availableOptionalMatieres
      };
    }
    
    return {
      quizGlobal: [],
      regular: availableRegularMatieres,
      specialty: availableSpecialtyMatieres,
      optional: availableOptionalMatieres
    };
  };

  // Fetch user's default level
  useEffect(() => {
    const fetchDefaultLevel = async () => {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('default_level')
          .eq('id', user.id)
          .single();

        if (error) throw error;

        if (data?.default_level) {
          setSelectedNiveau(data.default_level);
          
          // Auto-select Quiz global for college levels
          if (['6ème', '5ème', '4ème', '3ème'].includes(data.default_level)) {
            setSelectedMatiere('Quiz global');
          }
        }
      } catch (err) {
        console.error('Error fetching default level:', err);
      }
    };

    fetchDefaultLevel();
  }, [user]);

  useEffect(() => {
    // Initialize from URL parameters, but only if no default level was set
    const niveau = searchParams.get('niveau');
    const matiere = searchParams.get('matiere');
    const questions = parseInt(searchParams.get('questions') || '20');

    // Only override if we don't have a default level set
    if (niveau && !selectedNiveau) {
      setSelectedNiveau(niveau);
    }
    if (matiere && !selectedMatiere) {
      setSelectedMatiere(matiere);
    }
    setSelectedQuestionCount(questions);

    if ((selectedNiveau || niveau) && (selectedMatiere || matiere)) {
      fetchLeaderboard(questions, selectedNiveau || niveau || '', selectedMatiere || matiere || '');
    } else {
      setIsLoading(false);
    }
  }, [searchParams, selectedNiveau, selectedMatiere]);

  const fetchLeaderboard = async (questionCount: number, niveau: string, matiere: string) => {
    setIsLoading(true);
    try {
      console.log('🔍 Fetching leaderboard for:', { questionCount, niveau, matiere });

      // Fetch sessions with at least one question answered
      const { data: sessions, error: sessionsError } = await supabase
        .from('grand_quiz_sessions')
        .select('user_id, correct_answers, total_questions, current_question, score_percentage, completion_rate, completed_at, status, total_response_time_ms')
        .eq('total_questions', questionCount)
        .eq('niveau', niveau)
        .eq('matiere', matiere)
        .gt('current_question', 1) // Only sessions that have answered at least one question
        .order('completion_rate', { ascending: false }) // Sort by completion rate first
        .order('correct_answers', { ascending: false }) // Then by absolute score
        .order('total_response_time_ms', { ascending: true }) // Then by response time (faster is better)
        .order('completed_at', { ascending: true }) // Finally by completion time
        .limit(50);

      if (sessionsError) {
        console.error('❌ Sessions error:', sessionsError);
        throw sessionsError;
      }

      console.log('✅ Found sessions:', sessions?.length || 0);

      if (!sessions || sessions.length === 0) {
        console.log('⚠️ No sessions found');
        setLeaderboard([]);
        return;
      }

      // Get unique user IDs
      const userIds = [...new Set(sessions.map(session => session.user_id))];
      console.log('👥 Unique user IDs:', userIds.length, userIds);

      // Fetch all profiles
      const { data: allProfiles, error: allProfilesError } = await supabase
        .from('profiles')
        .select('id, first_name, email');

      if (allProfilesError) {
        console.error('❌ Error fetching all profiles:', allProfilesError);
      }

      // Filter profiles for users who have sessions
      let profiles = [];
      try {
        profiles = (allProfiles || []).filter(profile => userIds.includes(profile.id));
        console.log('👤 Found profiles:', profiles.length);
      } catch (profileError) {
        console.warn('⚠️ Profile fetch failed, continuing without profile data:', profileError);
      }

      // Create a map of user_id to display name for quick lookup
      const userDisplayMap = new Map();
      
      // Process profiles if we have them
      profiles.forEach(profile => {
        let displayName = 'Utilisateur anonyme';
        
        if (profile.first_name) {
          displayName = profile.first_name;
        } else if (profile.email) {
          displayName = profile.email.split('@')[0];
        }
        
        // Add "(vous)" for current user
        if (profile.id === user?.id) {
          displayName += ' (vous)';
        }
        
        userDisplayMap.set(profile.id, displayName);
      });

      // For users without profiles, create anonymous entries
      userIds.forEach(userId => {
        if (!userDisplayMap.has(userId)) {
          const displayName = userId === user?.id ? 'Utilisateur anonyme (vous)' : 'Utilisateur anonyme';
          userDisplayMap.set(userId, displayName);
        }
      });

      console.log('🏷️ User display map:', Array.from(userDisplayMap.entries()));

      // Process sessions and group by user (keep best score per user)
      const userBestScores = new Map();
      
      sessions.forEach(session => {
        const questionsAnswered = session.current_question - 1;
        const completionRate = session.completion_rate || 0;
        const scorePercentage = session.score_percentage || 0;
        const responseTime = session.total_response_time_ms || 0;
        const existing = userBestScores.get(session.user_id);
        
        // Determine if this session is better than the existing one
        const isBetter = !existing || 
          completionRate > existing.completion_rate || 
          (completionRate === existing.completion_rate && session.correct_answers > existing.score) ||
          (completionRate === existing.completion_rate && session.correct_answers === existing.score && responseTime < existing.total_response_time_ms);
        
        if (isBetter) {
          userBestScores.set(session.user_id, {
            user_id: session.user_id,
            score: session.correct_answers,
            total_questions: session.total_questions,
            questions_answered: questionsAnswered,
            completion_rate: completionRate,
            score_percentage: scorePercentage,
            total_response_time_ms: responseTime,
            completed_at: session.completed_at || session.updated_at,
            user_display_name: userDisplayMap.get(session.user_id) || 'Utilisateur anonyme'
          });
        }
      });

      console.log('🏆 User best scores:', userBestScores.size, Array.from(userBestScores.entries()));

      // Convert to array and sort with tie-breaking logic
      const leaderboardData: LeaderboardEntry[] = Array.from(userBestScores.values())
        .sort((a, b) => {
          // Primary sort: completion rate (descending)
          if (b.completion_rate !== a.completion_rate) {
            return b.completion_rate - a.completion_rate;
          }
          
          // Secondary sort: absolute score (descending)
          if (b.score !== a.score) {
            return b.score - a.score;
          }
          
          // Tertiary sort: response time (ascending - faster is better)
          if (a.total_response_time_ms !== b.total_response_time_ms) {
            return a.total_response_time_ms - b.total_response_time_ms;
          }
          
          // If everything is equal, they have the same rank
          return 0;
        });

      // Assign ranks with tie handling
      let currentRank = 1;
      for (let i = 0; i < leaderboardData.length; i++) {
        if (i > 0) {
          const current = leaderboardData[i];
          const previous = leaderboardData[i - 1];
          
          // Check if current entry should have the same rank as previous
          const sameCompletionRate = current.completion_rate === previous.completion_rate;
          const sameScore = current.score === previous.score;
          const sameResponseTime = current.total_response_time_ms === previous.total_response_time_ms;
          
          if (sameCompletionRate && sameScore && sameResponseTime) {
            // Same rank as previous
            leaderboardData[i].rank = previous.rank;
          } else {
            // New rank
            currentRank = i + 1;
            leaderboardData[i].rank = currentRank;
          }
        } else {
          // First entry gets rank 1
          leaderboardData[i].rank = 1;
        }
      }

      console.log('🥇 Final leaderboard data:', leaderboardData.length);
      console.log('📋 Top 5:', leaderboardData.slice(0, 5));

      setLeaderboard(leaderboardData);

    } catch (err) {
      console.error('❌ Error fetching leaderboard:', err);
      setError('Erreur lors du chargement du classement');
      setLeaderboard([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = async () => {
    if (selectedQuestionCount && selectedNiveau && selectedMatiere) {
      await fetchLeaderboard(selectedQuestionCount, selectedNiveau, selectedMatiere);
    }
  };

  useEffect(() => {
    if (selectedQuestionCount && selectedNiveau && selectedMatiere) {
      handleFilterChange();
    }
  }, [selectedQuestionCount, selectedNiveau, selectedMatiere]);

  const getScoreColor = (completionRate: number) => {
    if (completionRate === 100) return 'text-yellow-600';
    if (completionRate >= 80) return 'text-green-600';
    if (completionRate >= 60) return 'text-blue-600';
    return 'text-gray-600';
  };

  const getScoreBadge = (completionRate: number) => {
    if (completionRate === 100) return '🏆';
    if (completionRate >= 80) return '🥇';
    if (completionRate >= 60) return '🥈';
    return '🥉';
  };

  const formatResponseTime = (timeMs: number) => {
    if (timeMs === 0) return 'N/A';
    const seconds = Math.round(timeMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white shadow sm:rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:px-6 bg-gradient-to-r from-purple-600 to-blue-600">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white">Classement du Grand Quiz</h1>
              <p className="text-purple-100 mt-1">Découvrez les meilleurs scores de la communauté</p>
            </div>
            <Trophy className="h-12 w-12 text-yellow-300" />
          </div>
        </div>

        <div className="px-4 py-5 sm:p-6">
          {/* Filters */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label htmlFor="niveau" className="block text-sm font-medium text-gray-700 mb-1">
                Classe
              </label>
              <select
                id="niveau"
                value={selectedNiveau}
                onChange={(e) => {
                  const newNiveau = e.target.value;
                  setSelectedNiveau(newNiveau);
                  // Reset matiere when niveau changes to avoid invalid combinations
                  setSelectedMatiere('');
                }}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
              >
                <option value="">Sélectionner une classe</option>
                {niveaux.map(niveau => (
                  <option key={niveau} value={niveau}>{niveau}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="matiere" className="block text-sm font-medium text-gray-700 mb-1">
                Matière
              </label>
              <select
                id="matiere"
                value={selectedMatiere}
                onChange={(e) => {
                  const newMatiere = e.target.value;
                  if (newMatiere === 'Philosophie' && selectedNiveau !== 'Terminale') {
                    setSelectedNiveau('Terminale');
                  } else if (!isSubjectAvailable(newMatiere, selectedNiveau)) {
                    setSelectedNiveau('');
                  }
                  setSelectedMatiere(newMatiere);
                }}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                disabled={!selectedNiveau}
              >
                <option value="">Sélectionner une matière</option>
                {(() => {
                  const availableMatieres = getAvailableMatieres();
                  return (
                    <>
                      {availableMatieres.quizGlobal.length > 0 && (
                        <optgroup label="Quiz spéciaux">
                          {availableMatieres.quizGlobal.map(matiere => (
                            <option key={matiere} value={matiere}>{matiere}</option>
                          ))}
                        </optgroup>
                      )}
                      {availableMatieres.regular.length > 0 && (
                        <optgroup label="Matières générales">
                          {availableMatieres.regular.map(matiere => (
                            <option key={matiere} value={matiere}>{matiere}</option>
                          ))}
                        </optgroup>
                      )}
                      {availableMatieres.specialty.length > 0 && (
                        <optgroup label="Spécialités">
                          {availableMatieres.specialty.map(matiere => (
                            <option key={matiere} value={matiere}>{matiere}</option>
                          ))}
                        </optgroup>
                      )}
                      {availableMatieres.optional.length > 0 && (
                        <optgroup label="Options">
                          {availableMatieres.optional.map(matiere => (
                            <option key={matiere} value={matiere}>{matiere}</option>
                          ))}
                        </optgroup>
                      )}
                    </>
                  );
                })()}
              </select>
            </div>

            <div>
              <label htmlFor="questions" className="block text-sm font-medium text-gray-700 mb-1">
                Questions
              </label>
              <select
                id="questions"
                value={selectedQuestionCount}
                onChange={(e) => setSelectedQuestionCount(Number(e.target.value))}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
              >
                {questionCounts.map(count => (
                  <option key={count} value={count}>{count} questions</option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              <Link
                to="/grand-quiz"
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                Retour
              </Link>
            </div>
          </div>

          {/* Leaderboard */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
              <p className="text-gray-500">Chargement du classement...</p>
            </div>
          ) : !selectedNiveau || !selectedMatiere ? (
            <div className="text-center py-12">
              <Award className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Sélectionnez une classe et une matière pour voir le classement</p>
            </div>
          ) : leaderboard.length === 0 ? (
            <div className="text-center py-12">
              <Trophy className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Aucun résultat pour cette configuration</p>
              <p className="text-sm text-gray-400 mt-2">
                Soyez le premier à faire un Grand Quiz avec ces paramètres !
              </p>
            </div>
          ) : (
            <div>
              {/* Mobile-optimized leaderboard */}
              <div className="block sm:hidden">
                <div className="space-y-3">
                  {leaderboard.map((entry, index) => (
                    <div 
                      key={`${entry.user_id}-${entry.completed_at}`} 
                      className={`p-4 rounded-lg border ${
                        entry.user_id === user?.id 
                          ? 'bg-gradient-to-r from-purple-50 to-blue-50 border-purple-300' 
                          : 'bg-white border-gray-200'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center">
                          <span className="text-2xl mr-2">{getScoreBadge(entry.completion_rate)}</span>
                          <span className="font-bold text-lg">{entry.rank || (index + 1)}</span>
                          {(entry.rank || (index + 1)) === 1 && <span className="ml-2 text-yellow-500">👑</span>}
                        </div>
                        <div className="text-right">
                          <div className={`text-lg font-bold ${getScoreColor(entry.completion_rate)}`}>
                            {entry.score}
                          </div>
                          <div className="text-xs text-gray-500">bonnes réponses</div>
                        </div>
                      </div>
                      <div className="text-sm">
                        <span className={entry.user_id === user?.id ? 'font-bold text-purple-700' : 'text-gray-700'}>
                          {entry.user_display_name}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Desktop table */}
              <div className="hidden sm:block overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                        Rang
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Prénom
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                        Taux de bonnes réponses
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                        Bonnes réponses
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                        Temps total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {leaderboard.map((entry, index) => (
                      <tr key={`${entry.user_id}-${entry.completed_at}`} className={entry.user_id === user?.id ? 'bg-purple-50' : ''}>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                          <div className="flex items-center">
                            <span className="mr-2">{getScoreBadge(entry.completion_rate)}</span>
                            <span className="font-bold">{entry.rank || (index + 1)}</span>
                            {(entry.rank || (index + 1)) === 1 && <span className="ml-2 text-yellow-500">👑</span>}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          <span className={entry.user_id === user?.id ? 'font-bold text-purple-700' : ''}>
                            {entry.user_display_name}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
                          <span className={`font-medium ${getScoreColor(entry.completion_rate)}`}>
                            {entry.completion_rate.toFixed(1)}%
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-center font-medium">
                          {entry.score}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-center">
                          {formatResponseTime(entry.total_response_time_ms)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GrandQuizLeaderboardPage;